import { Controller } from "stimulus";

export default class EmailSpamScoreController extends Controller {
    static targets = ["badge", "value", "help"];

    connect() {
        this.nodeEl = this.element.closest("[id^='node-']");
        this.nodeId = this.nodeEl?.id?.replace("node-", "");

        this.url = this.element.dataset.url;
        this.threshold = parseFloat(this.element.dataset.threshold) || 4.6;

        this._onRevalidate = this.onRevalidate.bind(this);
        this.nodeEl?.addEventListener("spam-score:revalidate", this._onRevalidate);

        this.hydrateFromSnapshot();
        this.toggleVisibilityFromNode();
    }

    disconnect() {
        this.nodeEl?.removeEventListener("spam-score:revalidate", this._onRevalidate);
    }

    check() {
        if (!this.nodeEl || !this.url) {
            return Promise.resolve(this._unknownResult("Spam check unavailable."));
        }

        const subject   = this.nodeEl.querySelector("#email-subject")?.value || "";
        const htmlBody  = this.nodeEl.querySelector(".email-html")?.value || "";
        const fromEmail = this.nodeEl.querySelector("#email-from-email")?.value || "";

        if (!subject && !htmlBody) {
            const result = this._unknownResult("Not validated");
            this.paint({ severity: "unknown", value: "—", status: result.message });
            return Promise.resolve(result);
        }

        this.paint({ severity: "checking", value: "…", status: "Checking…" });

        this.lastRequestToken = Date.now();
        const requestToken = this.lastRequestToken;

        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        return fetch(this.url, {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": csrf
            },
            body: JSON.stringify({
                subject: subject,
                html_body: htmlBody,
                from_email: fromEmail
            })
        })
        .then(r => r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status)))
        .then(data => {
            if (requestToken === this.lastRequestToken) {
                this.paintFromResponse(data);
            }
            const severity = this._calculateSeverity(data.score);
            return {
                severity: severity,
                score:    data.score,
                message:  data.message || this.defaultStatusFor(severity)
            };
        })
        .catch(() => {
            if (requestToken === this.lastRequestToken) {
                this.paint({ severity: "unknown", value: "—", status: "Check failed — retry." });
            }
            return this._unknownResult("Check failed — retry.");
        });
    }

    checkForSubmit() {
        const current = this._currentSeverity();
        if (current === "good" || current === "bad") {
            return Promise.resolve({
                severity: current,
                score:    this._currentScore(),
                message:  this.hasBadgeTarget ? (this.badgeTarget.title || "") : ""
            });
        }
        return this.check();
    }

    nodeTitle() {
        return this.nodeEl?.querySelector("#email-subject")?.value || "Untitled email";
    }

    _unknownResult(message) {
        return { severity: "unknown", score: null, message: message };
    }

    _calculateSeverity(score) {
        if (score == null) return "unknown";

        // score > threshold: bad
        // score <= threshold - 1: good
        if (score > this.threshold) {
            return "bad";
        } else {
            return "good";
        }
    }

    _currentSeverity() {
        if (!this.hasBadgeTarget) return "unknown";
        const cls = this.badgeTarget.classList;
        if (cls.contains("spam-score-badge--good"))  return "good";
        if (cls.contains("spam-score-badge--bad"))   return "bad";
        if (cls.contains("spam-score-badge--checking")) return "checking";
        return "unknown";
    }

    _currentScore() {
        if (!this.hasValueTarget) return null;
        const v = parseFloat(this.valueTarget.textContent);
        return Number.isFinite(v) ? v : null;
    }

    // === Event handlers ====================================================

    onRevalidate() {
        // Defer slightly so the dispatching controller (sequence/BEE) has
        // already flushed the hidden inputs we read from.
        setTimeout(() => {
            this.toggleVisibilityFromNode();
            this.check();
        }, 50);
    }

    // === Rendering =========================================================

    paintFromResponse(data) {
        const severity = this._calculateSeverity(data.score);
        const score    = (data.score == null) ? "—" : Number(data.score).toFixed(2);
        const status   = data.message || this.defaultStatusFor(severity);
        this.paint({ severity, value: score, status });
    }

    paint({ severity, value, status }) {
        if (this.hasValueTarget) this.valueTarget.textContent = value;
        if (this.hasBadgeTarget) {
            this.badgeTarget.classList.remove(
                "spam-score-badge--good",
                "spam-score-badge--bad",
                "spam-score-badge--unknown",
                "spam-score-badge--checking"
            );
            this.badgeTarget.classList.add(`spam-score-badge--${severity}`);
            this.badgeTarget.title = status;
        }
        if (this.hasHelpTarget) this.helpTarget.title = status;
    }

    defaultStatusFor(severity) {
        switch (severity) {
            case "good": return "Looks good";
            case "bad":  return "High spam risk";
            default:     return "Not validated";
        }
    }

    // === Hydration / visibility ===========================================

    hydrateFromSnapshot() {
        const blob = document.getElementById("campaign-spam-scores");
        if (!blob || !this.nodeId) return;

        let parsed;
        try { parsed = JSON.parse(blob.textContent || "{}"); }
        catch { return; }

        const entry = parsed[this.nodeId];
        if (!entry || entry.score == null) return;

        const severity = this._calculateSeverity(entry.score);
        this.paint({
            severity: severity,
            value:    Number(entry.score).toFixed(2),
            status:   entry.reason || this.defaultStatusFor(severity)
        });
    }

    toggleVisibilityFromNode() {
        if (!this.nodeEl) return;
        const preview = this.nodeEl.querySelector(".email-body-preview");
        const hasContent = preview?.classList.contains("has-content");
        this.element.style.display = hasContent ? "" : "none";
    }
}
